import React from "react";
import Menu from "@mui/material/Menu";

export default function MenuSurface({
  anchorEl,
  onClose,
  children,
  id,
  listRef,
  autoFocus = true,
  positionAnchorEl = anchorEl,
  anchorOrigin = { vertical: "bottom", horizontal: "left" },
  transformOrigin = { vertical: "top", horizontal: "left" },
  fitBelow = false,
  paperSx,
}) {
  const [placement, setPlacement] = React.useState({
    height: 320,
    above: false,
  });
  React.useLayoutEffect(() => {
    if (!fitBelow || !positionAnchorEl) return;
    const updateHeight = () => {
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const bottom = viewport
        ? viewport.height + viewport.offsetTop
        : window.innerHeight;
      const rect = positionAnchorEl.getBoundingClientRect();
      const belowSpace = Math.max(0, bottom - rect.bottom - 16);
      const aboveSpace = Math.max(0, rect.top - top - 16);
      const above = belowSpace < 160 && aboveSpace > belowSpace;
      const space = above ? aboveSpace : belowSpace;
      // Let MUI reposition into the viewport if the trigger is entirely offscreen.
      const height = Math.max(1, Math.min(320, space || bottom - top - 32));
      setPlacement((previous) =>
        previous.height === height && previous.above === above
          ? previous
          : { height, above },
      );
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("scroll", updateHeight, true);
    window.visualViewport?.addEventListener("resize", updateHeight);
    window.visualViewport?.addEventListener("scroll", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", updateHeight, true);
      window.visualViewport?.removeEventListener("resize", updateHeight);
      window.visualViewport?.removeEventListener("scroll", updateHeight);
    };
  }, [positionAnchorEl, fitBelow]);
  React.useEffect(() => {
    return () => {
      if (document.activeElement === document.body && anchorEl?.isConnected)
        anchorEl.focus();
    };
  }, [anchorEl]);
  const flatten = (items, prefix = "") =>
    React.Children.toArray(items).flatMap((child) =>
      child.type === React.Fragment
        ? flatten(child.props.children, `${prefix}${child.key}:`)
        : [React.cloneElement(child, { key: `${prefix}${child.key}` })],
    );
  return (
    <Menu
      open
      autoFocus={autoFocus}
      disableAutoFocusItem={!autoFocus}
      disableRestoreFocus
      anchorEl={positionAnchorEl}
      {...(fitBelow && placement.above
        ? {
            anchorOrigin: {
              horizontal: anchorOrigin?.horizontal ?? "left",
              vertical: "top",
            },
            transformOrigin: {
              horizontal: transformOrigin?.horizontal ?? "left",
              vertical: "bottom",
            },
          }
        : {
            ...(anchorOrigin ? { anchorOrigin } : {}),
            ...(transformOrigin ? { transformOrigin } : {}),
          })}
      onClose={onClose}
      onClick={(event) => event.stopPropagation()}
      slotProps={{
        list: {
          id,
          ref: listRef,
          sx: {
            "& .MuiMenuItem-root": {
              width: "100%",
              boxSizing: "border-box",
              textAlign: "left",
              justifyContent: "flex-start",
              whiteSpace: "normal",
            },
            "@media (max-width: 600px)": {
              "& .MuiMenuItem-root.desktop-only": { display: "none" },
            },
          },
        },
        paper: {
          sx: [
            {
              minWidth: 200,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: fitBelow
                ? placement.height
                : "min(320px, calc(100dvh - 32px))",
            },
            paperSx,
          ],
        },
      }}
    >
      {flatten(children)}
    </Menu>
  );
}
